import { ReactComponent as HandleArrowDownIcon } from '@assets/images/app/handle-arrow-down.svg';
import { CalendarCartLine } from '@utils/product-option-date';
import React from 'react';

interface ProductCalendarCartSheetProps {
  open: boolean;
  lines: CalendarCartLine[];
  onClose: () => void;
  onRemove: (id: string) => void;
  onPurchase: () => void;
}

/** 담은 옵션 목록 바텀시트 — CTA 상단에서 올라오도록 bottom 고정 */
export const ProductCalendarCartSheet: React.FC<ProductCalendarCartSheetProps> = ({
  open,
  lines,
  onClose,
  onRemove,
  onPurchase,
}) => {
  const totalFee = lines.reduce((sum, line) => sum + line.lineTotal, 0);
  const totalCount = lines.reduce((sum, line) => sum + line.optionCount, 0);
  const sheetHeight = Math.min(640, Math.max(280, 120 + lines.length * 120));

  return (
    <div
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        onClose();
      }}
      style={{
        transition: 'opacity 0.3s ease',
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 99999,
        overflow: 'hidden',
      }}>
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: sheetHeight,
          maxHeight: '100%',
          width: '100%',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px 8px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
        }}>
        <button
          type="button"
          className="link-button"
          onClick={onClose}
          style={{
            width: '100%',
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-label="닫기">
          <HandleArrowDownIcon width={44} height={10} />
        </button>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 8px' }}>
          {lines.map((line) => {
            const additionalText = line.additionalOptions.map((item) => `${item.name} X${item.count}`).join(' / ');
            return (
              <div
                key={line.id}
                style={{
                  background: '#FFFFFF',
                  borderRadius: 12,
                  border: '1px solid #EFF1F4',
                  padding: 16,
                  marginBottom: 10,
                  position: 'relative',
                }}>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => onRemove(line.id)}
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#EFF1F4',
                    color: '#66686B',
                    fontSize: 14,
                    lineHeight: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="삭제">
                  ×
                </button>
                <div
                  style={{
                    paddingRight: 28,
                    color: '#242424',
                    fontSize: 14,
                    lineHeight: '20px',
                    fontWeight: 500,
                  }}>
                  옵션-{line.optionName} X{line.optionCount}
                </div>
                {additionalText && (
                  <div style={{ marginTop: 4, color: '#66686B', fontSize: 13, lineHeight: '18px' }}>
                    추가 옵션-{additionalText}
                  </div>
                )}
                <div
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <span style={{ color: '#66686B', fontSize: 14 }}>합계</span>
                  <span style={{ color: '#242424', fontSize: 16, fontWeight: 700 }}>
                    {line.lineTotal.toLocaleString()}원
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '8px 16px 16px', flexShrink: 0 }}>
          <button
            type="button"
            className="link-button"
            disabled={lines.length === 0}
            onClick={onPurchase}
            style={{
              width: '100%',
              height: 56,
              borderRadius: 8,
              background: lines.length === 0 ? '#D5D8DD' : 'var(--Primary-95, #FF3D8F)',
              color: '#FFFFFF',
              fontSize: 16,
              fontWeight: 700,
              lineHeight: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: '#FFFFFF',
                color: 'var(--Primary-95, #FF3D8F)',
                fontSize: 14,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {totalCount}
            </span>
            <span>{totalFee.toLocaleString()}원 구매하기</span>
          </button>
        </div>
      </div>
    </div>
  );
};
